/**
 * Cmarkdownxychart — hover popover
 *
 * Glassy floating panel that lists every series's value at the hovered
 * category, with a subtle color swatch and label. Designed to be readable
 * at a glance and stay calm while hovering moves it around.
 */
'use client';
import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { XYCHART_POPOVER_BACKDROP, XYCHART_POPOVER_BACKGROUND, XYCHART_POPOVER_BORDER, XYCHART_POPOVER_SHADOW, } from './xychart-constants';
import { CmarkdownLabelText } from '../Cmarkdowntext/CmarkdownLabelText';
import { FontWeight, getFontFamily } from '@/theme/typography';
const CATEGORY_VALUE = "category";
export interface XYChartHoverEntry {
    seriesIndex: number;
    label: string;
    value: number;
    color: string;
}
export interface XYChartHoverPopoverProps {
    category: string;
    entries: XYChartHoverEntry[];
    formatValue: (value: number) => string;
}
function getPopoverEntryIdentity(entry: XYChartHoverEntry): string {
    return `${entry.label}--${entry.seriesIndex}`;
}
const CSS_INHERIT = 'inherit';
const POPOVER_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 156,
    maxWidth: 260,
    padding: '10px 12px',
    borderRadius: 10,
    backgroundColor: XYCHART_POPOVER_BACKGROUND,
    border: XYCHART_POPOVER_BORDER,
    boxShadow: XYCHART_POPOVER_SHADOW,
    backdropFilter: XYCHART_POPOVER_BACKDROP,
    WebkitBackdropFilter: XYCHART_POPOVER_BACKDROP,
    color: 'rgba(255, 255, 255, 0.9)',
    fontFamily: getFontFamily(),
    pointerEvents: 'none',
};
const HEADER_STYLE: CSSProperties = {
    fontSize: 10,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.45)',
    fontWeight: 600,
    marginBottom: 2,
};
const ENTRY_ROW_STYLE: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
};
const ENTRY_LEFT_STYLE: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
};
const ENTRY_VALUE_STYLE: CSSProperties = {
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: '-0.005em',
    fontVariantNumeric: 'tabular-nums',
    color: 'rgba(255, 255, 255, 0.96)',
};
export function XYChartHoverPopover({ category, entries, formatValue, }: XYChartHoverPopoverProps) {
    const categoryDebugIdentity = category || CATEGORY_VALUE;
    return (
      <motion.div data-el="Cmarkdownxychart/HoverPopover/Root" initial={{ opacity: 0, y: 4, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 4, scale: 0.98 }} transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }} style={POPOVER_STYLE}>
        <div style={HEADER_STYLE}>
          <CmarkdownLabelText dataElScope="Cmarkdownxychart/XYChartHoverPopover" content={category} size={10} weight={FontWeight.SemiBold} paragraphStyle={{ marginBottom: 0 }} textStyle={{
            color: CSS_INHERIT,
            letterSpacing: CSS_INHERIT,
            textTransform: CSS_INHERIT,
        }}/>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entries.map((entry) => (<div data-el="Cmarkdownxychart/HoverPopover/Entry" key={`hover-entry-${entry.seriesIndex}-${entry.label}`} style={ENTRY_ROW_STYLE}>
              <div style={ENTRY_LEFT_STYLE}>
                <span style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: entry.color,
                flexShrink: 0,
            }}/>
                <CmarkdownLabelText dataElScope="Cmarkdownxychart/XYChartHoverPopover" content={entry.label} size={12} weight={FontWeight.Medium} style={{ minWidth: 0 }} paragraphStyle={{ marginBottom: 0, minWidth: 0 }} textStyle={{
                color: 'rgba(255, 255, 255, 0.78)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}/>
              </div>
              <span style={ENTRY_VALUE_STYLE}>
                {formatValue(entry.value)}
              </span>
            </div>))}
        </div>
      </motion.div>
    );
}
